import { modK } from '../lib/platform'
import { useState, type CSSProperties } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatLongDate, semesterWeek, type ISODate } from '@app/shared'

import { useClock, useTerm } from '../lib/data'
import { DataModeBadge, HeaderBand } from '../components/HeaderBand'
import { Search } from '../components/icons'
import { RecentSuggestions } from '../components/RecentSuggestions'
import { JournalEntryCard } from '../components/today/JournalEntryCard'
import {
  ClassesCard,
  DueSoonCard,
  LearningCard,
  RemindersCard,
} from '../components/today/ContextCards'

/**
 * TODAY — docs/design/handoff/"LifeOS Home Options.dc.html", option 3c.
 *
 * The home screen, and the product's identity: the journal entry is the page, and school,
 * deadlines, learning and reminders are context arranged around it. Anything that adds friction
 * to writing loses this argument (CLAUDE.md → Product identity rules).
 *
 * THE PAGE NEVER SCROLLS. The composer takes its natural height and the 2x2 context grid takes
 * everything that is left, so there is no dead lower half and nothing to scroll past. Every
 * overflow is scoped inside a card.
 */
export function Today() {
  const { data: clock } = useClock()
  const { data: term } = useTerm()
  const week = clock && term ? semesterWeek(clock.date, term.instructionStarts) : 0

  return (
    <>
      <HeaderBand tall>
        <div className="flex flex-none items-center gap-3 whitespace-nowrap">
          <span className="text-[12px] font-medium leading-none text-ink-2">
            {clock ? formatLongDate(clock.date) : '…'}
          </span>
          {/* No term in remote mode until the Canvas connector lands (V2) — so no week label,
              rather than a made-up one. */}
          {term && (
            <span className="rounded-pill border border-line bg-surface px-[9px] py-1 text-[10.5px] font-medium leading-[1.4] text-ink-2">
              {week > 0 ? `Week ${week} · ${term.name}` : term.name}
            </span>
          )}
          {/* The Day page's way in is its own rail entry now (day-page handoff, owner decision:
              "its own rail entry, directly after Today"). The phone's Today keeps its link to the
              prototype, which the phone still shows. */}
        </div>

        <HeaderSearch today={clock?.date} />
        <DataModeBadge />
      </HeaderBand>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden px-[30px] pb-[26px]">
        {clock && <JournalEntryCard date={clock.date} nowMinutes={clock.minutesIntoDay} />}
        {clock && (
          <div className="grid min-h-0 flex-1 grid-cols-2 grid-rows-2 gap-4">
            <DueSoonCard today={clock.date} nowMinutes={clock.minutesIntoDay} />
            <ClassesCard date={clock.date} nowMinutes={clock.minutesIntoDay} />
            <LearningCard date={clock.date} />
            <RemindersCard today={clock.date} />
          </div>
        )}
      </div>
    </>
  )
}

/**
 * SEARCH IS NOT A RAIL ITEM — it rides this field (the orchestrator decision recorded in
 * docs/design/FIDELITY.md). Submitting hands the query to `/search`, which is honest about
 * landing in V4 rather than pretending to answer.
 */
function HeaderSearch({ today }: { today?: ISODate }) {
  const [query, setQuery] = useState('')
  /**
   * Whether the recent list is showing. Focus opens it, blur and Escape close it, and picking a
   * row closes it on the way out.
   *
   * It gates MOUNTING, not just visibility, because the panel's three reads start when it mounts
   * (components/RecentSuggestions.tsx) — a Today nobody searched from should not pay for them.
   */
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()

  return (
    <form
      role="search"
      onSubmit={(e) => {
        e.preventDefault()
        setOpen(false)
        navigate(query.trim() ? `/search?q=${encodeURIComponent(query.trim())}` : '/search')
      }}
      style={{ '--mo-rest-shadow': 'var(--shadow-field)' } as CSSProperties}
      className="mo-field relative flex min-w-0 max-w-[400px] flex-1 items-center gap-[10px] rounded-tile border border-line-2 bg-surface/[0.86] px-[14px] py-[10px] shadow-field"
    >
      <Search size={15} strokeWidth={1.8} className="flex-none text-microcopy" />
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        // Escape closes the list without clearing what was typed: the query is the owner's work,
        // and dismissing a suggestion panel is not a request to throw it away.
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false)
        }}
        role="combobox"
        aria-expanded={open}
        aria-controls="recent-suggestions"
        placeholder="Search entries, classes, notes"
        aria-label="Search entries, classes, notes"
        className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[13px] leading-none text-ink outline-none placeholder:text-muted"
      />
      <kbd className="flex-none font-mono text-[10.5px] font-medium leading-none text-microcopy">
        {modK}
      </kbd>
      {open && (
        <div id="recent-suggestions" className="contents">
          <RecentSuggestions today={today} query={query} onPick={() => setOpen(false)} />
        </div>
      )}
    </form>
  )
}
